/**
 * Persists transient direct-form editor state in sessionStorage so a refresh
 * on /directForms/:id can restore the in-memory form (not stored on the server).
 */

const STORAGE_KEY_PREFIX = 'truqc_directForm_';
const normalizeFormId = value => value == null || value === '' ? '' : String(value).toUpperCase();
const storageKey = formId => "".concat(STORAGE_KEY_PREFIX).concat(normalizeFormId(formId));
const readSessionStorage = key => {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(key);
  } catch (_unused) {
    return null;
  }
};
const writeSessionStorage = (key, value) => {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    sessionStorage.setItem(key, value);
    return true;
  } catch (_unused2) {
    return false;
  }
};
const removeSessionStorage = key => {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    sessionStorage.removeItem(key);
    return true;
  } catch (_unused3) {
    return false;
  }
};
export const getDirectFormTypeIdentifier = active => {
  var _active$content, _active$formType;
  return (active === null || active === void 0 || (_active$content = active.content) === null || _active$content === void 0 || (_active$content = _active$content.formType) === null || _active$content === void 0 ? void 0 : _active$content.identifier) || (active === null || active === void 0 || (_active$formType = active.formType) === null || _active$formType === void 0 ? void 0 : _active$formType.identifier) || null;
};

/**
 * @param {string} formId
 * @param {{ active: object, pendingChanges?: boolean }} snapshot
 */
export const saveDirectFormSession = (formId, snapshot) => {
  var _snapshot$active;
  const id = normalizeFormId(formId);
  const activeId = normalizeFormId(snapshot === null || snapshot === void 0 || (_snapshot$active = snapshot.active) === null || _snapshot$active === void 0 ? void 0 : _snapshot$active.id);
  // Only persist snapshots that loadDirectFormSession can actually restore:
  // require a matching, non-empty active.id so we never store a snapshot that
  // load would reject (and never return true for unrestorable data).
  if (!id || !activeId || activeId !== id) return false;
  const formTypeIdentifier = getDirectFormTypeIdentifier(snapshot.active);
  return writeSessionStorage(storageKey(id), JSON.stringify({
    active: snapshot.active,
    pendingChanges: Boolean(snapshot.pendingChanges),
    formTypeIdentifier
  }));
};

/**
 * @param {string} formId
 * @returns {{ active: object, pendingChanges: boolean, formTypeIdentifier: string|null }|null}
 */
export const loadDirectFormSession = formId => {
  const id = normalizeFormId(formId);
  if (!id) return null;
  const raw = readSessionStorage(storageKey(id));
  if (!raw) return null;
  try {
    var _parsed$active;
    const parsed = JSON.parse(raw);
    if (!(parsed !== null && parsed !== void 0 && (_parsed$active = parsed.active) !== null && _parsed$active !== void 0 && _parsed$active.id)) return null;
    if (normalizeFormId(parsed.active.id) !== id) return null;
    return {
      active: parsed.active,
      pendingChanges: Boolean(parsed.pendingChanges),
      formTypeIdentifier: parsed.formTypeIdentifier || getDirectFormTypeIdentifier(parsed.active)
    };
  } catch (_unused4) {
    return null;
  }
};
export const removeDirectFormSession = formId => {
  const id = normalizeFormId(formId);
  if (!id) return false;
  return removeSessionStorage(storageKey(id));
};
export { normalizeFormId as normalizeDirectFormId };